import React, { FC, Fragment, useState } from 'react';
import { Timeline } from '@mui/lab';
import OrderEventRecord from 'components/shared/order-events-timeline/order-event-record.component';
import { EventOrgTypeFilter, OrderEvent, OrderEventFilters, OrderEventType } from 'models/order-event';
import { useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { getOrderEventsTemplate } from 'apollo/templates/order';
import Loader from 'components/shared/loader/loader.component';
import ImageGalleryDialog from 'components/shared/image-gallery-dialog/image-gallery-dialog.component';
import { matchesOrganizationTypeFilter } from 'components/shared/order-events-timeline/event-organization';

type OrderEventsTimelineProps = {
    orderIds: string[];
    visibleOrderIds?: string[];
    filters: OrderEventFilters;
    isMultipleOrders: boolean;
    organizationTypeFilter?: EventOrgTypeFilter;
};
const OrderEventsTimeline: FC<OrderEventsTimelineProps> = ({
    orderIds,
    visibleOrderIds,
    filters,
    isMultipleOrders,
    organizationTypeFilter,
}: OrderEventsTimelineProps) => {
    const [showImageGallery, setShowImageGallery] = useState(false);
    const [allImages, setAllImages] = useState<string[]>([]);
    const [selectedImage, setSelectedImage] = useState<string>();
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { orderEvents } = {}, loading } = useQuery(getOrderEventsTemplate, {
        variables: {
            orderIds: orderIds,
            filters: filters,
        },
        onCompleted: ({ orderEvents }) => {
            prepareImages(orderEvents);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const prepareImages = (orderEvents: [OrderEvent]) => {
        const images: string[] = [];
        orderEvents.map((event) => {
            if (event.type === OrderEventType.FILES_ADDED) {
                event.files?.map((file) => {
                    if (file && /image/.test(file.mimetype)) {
                        images.push(file.url);
                    }
                });
            }
        });
        setAllImages(images);
    };

    const handleImageClick = (imageUrl: string) => {
        setSelectedImage(imageUrl);
        setShowImageGallery(true);
    };

    const checkOrganizationFilter = (item: OrderEvent) => {
        if (organizationTypeFilter) {
            return matchesOrganizationTypeFilter(item, organizationTypeFilter);
        }
        return true;
    };

    const checkOrderVisible = (item: OrderEvent) => {
        if (visibleOrderIds) {
            return visibleOrderIds.includes(item.orderDbId);
        }
        return true;
    };

    return (
        <Fragment>
            {!loading ? (
                <Timeline sx={{ padding: 0, margin: 0 }}>
                    {orderEvents.map((item: OrderEvent, index: number) => (
                        <Fragment key={index}>
                            {checkOrganizationFilter(item) && checkOrderVisible(item) ? (
                                <OrderEventRecord
                                    item={item}
                                    isMultipleOrders={isMultipleOrders}
                                    last={index >= orderEvents.length - 1}
                                    onImageClick={handleImageClick}
                                />
                            ) : undefined}
                        </Fragment>
                    ))}
                </Timeline>
            ) : (
                <Loader />
            )}

            <ImageGalleryDialog
                imageUrls={allImages}
                selectedImageUrl={selectedImage}
                open={showImageGallery}
                onClose={() => {
                    setShowImageGallery(false);
                }}
            />
        </Fragment>
    );
};

export default OrderEventsTimeline;
